import { BasicFieldReference } from "../../models/BasicField";
import Call from "../../models/Call";
import { CallDisconnectReason } from "../../models/CallDisconnectReason";
import { LeadListMode } from "../../models/LeadListMode";
import { IncomingCall } from "../../models/IncomingCall";
import PhoneNumber from "../../models/PhoneNumber";

export type TelephonyServiceError = { [k: string]: any, retryable?: boolean; };

export enum TelephonyServiceStatus {
  /**
   * The service has not yet been initialized.
   */
  Initializing = 'initializing',

  /**
   * The service has been initialized, but we are necessarily not connected.
   */
  Initialized = 'initialized',

  /**
   * The service has been initialized and we are connected.
   */
  Connected = 'connected'
}

export type CallMetadata = {
  contactId?: string;
  /**
   * Note: Must not be the ID of a shuffle lead list, but rather the source lead list.
   * Set `shuffleLeadListId` to the ID of the shuffle lead list.
   */
  leadListId?: string;
  shuffleLeadListId?: string;
  leadListMode: LeadListMode;
  projectId?: string;
  pendingCallId?: string;
}

export type TelephonyServiceRef = {
  call: (contactPhoneNumber: string, caller: PhoneNumber, metadata: CallMetadata) => Promise<void>;
  hangUp: (options?: HangUpOptions) => void;
  cohearUser: (userId: string) => Promise<void>;
  cohearCall: (call: Call) => Promise<string>;
  sendDigit: (digit: string) => void;
  startPredictiveMode: (leadListId: string) => Promise<void>;

  /**
   * Answers an inbound or transferred call.
   */
  answerCall: (call: IncomingCall) => Promise<void>;
}

export type TelephonyServiceProps = {
  _ref: (ref: TelephonyServiceRef) => void;

  /**
   * Called when a call is ringing
   */
  onCallConnecting: (info: TelephonyServiceCallInfo) => void;

  /**
   * Called when a call was accepted and connected has been initiated.
   */
  onCallConnected: (info: TelephonyServiceCallInfo) => void;

  /**
   * Called when call was disconnected.
   */
  onCallDisconnected: (info: TelephonyServiceCallInfo) => void;

  /**
   * Called when the user is connected to the service.
   */
  onServiceConnected: () => void;

  /**
   * Called when the service is initialized.
   */
  onServiceInitialized: () => void;

  /**
   * Called if an error occurred with the service.
   */
  onServiceError: (error?: any) => void;

  /**
   * Called when the user was disconnected from the service.
   */
  onServiceDisconnected: () => void;

  /**
   * Called when an error occured when attempting to make the call.
   */
  onCallError: (reason: string | null | undefined, error: any) => void;

  onRemoteStream?: (stream: MediaStream) => void;
}

export type TelephonyServiceCallInfo = {
  contactPhoneNumber: string;
  callerPhoneNumber: string;
  easyTelecomUserCallId?: string;
  callId?: string;
  contactId?: string;
  contactFields?: BasicFieldReference[];
  /**
   * Whether call was recovered, for example after closing the laptop screen.
   */
  isRecovered?: boolean;
};

export type HangUpOptions = {

  /**
   * Reason for hanging up the call.
   */
  reason: CallDisconnectReason;
}